import { Injectable } from '@angular/core';
import { LoggerService } from 'src/app/services/logger.service'
import { Store } from "@ngxs/store";
import { ExpenseTypeActions} from '../state-man/actions/expense-type.actions';
import { UuidAndId } from '../state-man/models/uuid-and-id';
import { UiService } from './ui.service';
import { ISPCStoreActions } from '../state-man/actions/ispc-store.actions';
import { User } from '../state-man/models/user.model';
import { ProjectTypeActions } from '../state-man/actions/project-type.actions';
import { TimeEntry } from '../state-man/models/time-entry.model';
import { TimeEntryService } from './time-entry.service';
import { TimeEntryActions } from '../state-man/actions/time-entry.actions';
import { ExpenseEntryService } from './expense-entry.service';
import { ExpenseEntryActions } from '../state-man/actions/expense-entry.actions';
import { ExpenseEntry } from '../state-man/models/expense-entry.model';
import { ConfigActions } from '../state-man/actions/config.actions';

@Injectable({
  providedIn: 'root'
})
export class OfflineService {
  is_processing_time_entries = false  
  is_processing_expense_entries = false
  
  constructor(    
    private timeEntryService: TimeEntryService,
    private expenseEntryService: ExpenseEntryService,  
    private store: Store,
    private uiService: UiService    
    ) {}
  
  async processPendingTimeEntries(timeEntries: TimeEntry[]) {
    try {
      if (!this.is_processing_time_entries) {
        this.is_processing_time_entries = true
        await this.uploadPendingTimeEntryDeletes(timeEntries)
        await this.uploadPendingTimeEntrySaves(timeEntries)
        this.is_processing_time_entries = false
      }
    } catch(e) {
      LoggerService.log(e)
      this.is_processing_time_entries = false
    }
  }

  async processPendingExpenseEntries(expenseEntries: ExpenseEntry[]) {
    try {
      if (!this.is_processing_expense_entries) {
        this.is_processing_expense_entries = true
        await this.uploadPendingExpenseEntryDeletes(expenseEntries)
        await this.uploadPendingExpenseEntrySaves(expenseEntries)
        this.is_processing_expense_entries = false
      }
    } catch(e) {
      LoggerService.log(e)
      this.is_processing_expense_entries = false
    }
  }
  
  async uploadPendingTimeEntrySaves(timeEntries: TimeEntry[]) {    
    try {              
        for (const timeEntry of timeEntries) {  
          if (timeEntry.is_pending_save) {
            if (timeEntry.id <= 0) {
              const apiObj = await this.timeEntryService.addTimeEntry(timeEntry)  
              if (apiObj) {
                const id: number = apiObj.id
                const uuid: string = timeEntry.uuid
                const payload: TimeEntry = TimeEntry.buildOne({...apiObj, uuid, id})
                this.store.dispatch(new TimeEntryActions.ApiAddedOne(payload))  
              } 
            } else {
              const apiObj = await this.timeEntryService.updateTimeEntry(timeEntry)  
              if (apiObj) {
                const id: number = apiObj.id
                const uuid: string = timeEntry.uuid
                const payload: TimeEntry = TimeEntry.buildOne({...apiObj, uuid, id})
                this.store.dispatch(new TimeEntryActions.ApiUpdatedOne(payload))  
              } 
            }
          }
        }                    
    } catch (error) {            
      LoggerService.log(error)   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
    } 
  }

  async uploadPendingTimeEntryDeletes(timeEntries: TimeEntry[]) {    
    try {              
        for (const timeEntry of timeEntries) {  
          if (timeEntry.is_pending_delete) {            
            const status = await this.timeEntryService.deleteTimeEntry(timeEntry)              
            if (status && status === 'OK') {
              const id: number = timeEntry.id
              const uuid: string = timeEntry.uuid
              const payload: TimeEntry = TimeEntry.buildOne({uuid, id})
              this.store.dispatch(new TimeEntryActions.ApiDeletedOne(payload))  
            }             
          }
        }                    
    } catch (error) {            
      LoggerService.log(error)   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
    } 
  }

  async uploadPendingExpenseEntrySaves(expenseEntries: ExpenseEntry[]) {    
    try {              
        for (const expenseEntry of expenseEntries) {  
          if (expenseEntry.is_pending_save) {
            if (expenseEntry.id <= 0) {
              const apiObj = await this.expenseEntryService.addExpenseEntry(expenseEntry)  
              if (apiObj) {
                const id: number = apiObj.id
                const uuid: string = expenseEntry.uuid
                const payload: ExpenseEntry = ExpenseEntry.buildOne({...apiObj, uuid, id})
                this.store.dispatch(new ExpenseEntryActions.ApiAddedOne(payload))  
              } 
            } else {
              const apiObj = await this.expenseEntryService.updateExpenseEntry(expenseEntry)  
              if (apiObj) {
                const id: number = apiObj.id
                const uuid: string = expenseEntry.uuid
                const payload: ExpenseEntry = ExpenseEntry.buildOne({...apiObj, uuid, id})
                this.store.dispatch(new ExpenseEntryActions.ApiUpdatedOne(payload))  
              } 
            }
          }
        }                    
    } catch (error) {            
      LoggerService.log(error)   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
    } 
  }

  async uploadPendingExpenseEntryDeletes(expenseEntries: ExpenseEntry[]) {    
    try {              
        for (const expenseEntry of expenseEntries) {  
          if (expenseEntry.is_pending_delete) {            
            const status = await this.expenseEntryService.deleteExpenseEntry(expenseEntry)              
            if (status && status === 'OK') {
              const id: number = expenseEntry.id
              const uuid: string = expenseEntry.uuid
              const payload: UuidAndId = {uuid, id}                   
              this.store.dispatch(new ExpenseEntryActions.ApiDeletedOne(payload))  
            }             
          }
        }                    
    } catch (error) {            
      LoggerService.log(error)   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
    } 
  }
 
  async refresh(user: User) {
    try {            
      this.store.dispatch(new ConfigActions.Refresh())
      this.store.dispatch(new ExpenseTypeActions.Refresh())             
      this.store.dispatch(new ISPCStoreActions.Refresh(user))
      this.store.dispatch(new ProjectTypeActions.Refresh())
      this.store.dispatch(new TimeEntryActions.Refresh())
      this.store.dispatch(new ExpenseEntryActions.Refresh())

      // Do this to trigger uploading pending data
      await this.trigger_pending()
      //this.uiService.stopWait()
    } catch (e) {
      //this.uiService.stopWait()
      LoggerService.log(e)
    }  
  }  

  async trigger_pending() {
    try {
      // Do this to trigger uploading pending data
      this.store.dispatch(new TimeEntryActions.IncrementPendingCount())
      this.store.dispatch(new ExpenseEntryActions.IncrementPendingCount())
    } catch (e) {
      LoggerService.log(e)
    }  
  }  

}
